// src/features/fx-ranking/components/FXDetailPopup.tsx
//
// Tabbed detail modal for a single FX currency. Opened by clicking a ticker
// in the universe table. Supersedes the standalone <CotPopup/> by nesting the
// existing COT view as one of four tabs:
//   - PRICE   : daily USD-numeraire close (area) + broker tick volume (bars)
//               from GET /api/v1/fx-ranking/price-history/{symbol}.
//   - SIGNAL  : composite Forge Ultra Signal line from the factor history.
//   - FACTORS : momentum / Commercials / Speculators component z-scores
//               (lines) + annualized volatility (bars) from
//               GET /api/v1/fx-ranking/factors/{symbol}.
//   - COT     : weekly Commercial vs Non-Commercial net positioning, reusing
//               the presentational <CotChartBody/> extracted from CotPopup.
//
// Price + factor history are fetched once on open (in parallel); COT history
// is fetched via the shared useCotHistory hook so it carries the identical
// loading / error / empty-state semantics as the standalone pop-over.

import { type ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    fetchFactors,
    fetchPriceHistory,
    type FactorsResponse,
    type PriceHistoryResponse,
} from '../api';
import { CotChartBody, useCotHistory } from './CotPopup';

type TabKey = 'price' | 'signal' | 'factors' | 'cot';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'price', label: 'Price / Volume' },
    { key: 'signal', label: 'Signal' },
    { key: 'factors', label: 'Factors' },
    { key: 'cot', label: 'COT' },
];

const HISTORY_DAYS = 365;

// FX rates span several orders of magnitude (JPY ~0.0067, GBP ~1.27), so a
// fixed-decimal formatter is unsuitable. Use significant figures instead.
const formatPrice = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    return value.toPrecision(5);
};

const formatVolume = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return 'N/A';
    const abs = Math.abs(value);
    if (abs >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${(value / 1e3).toFixed(1)}k`;
    return value.toFixed(0);
};

const formatZ = (value: number | null): string =>
    value == null || !Number.isFinite(value) ? 'N/A' : value.toFixed(2);

interface ChartStateProps {
    isLoading: boolean;
    error: string | null;
    isEmpty: boolean;
    emptyMessage: string;
    children: ReactNode;
}

// Shared loading / error / empty gate so each tab body stays declarative.
const ChartState = ({
    isLoading,
    error,
    isEmpty,
    emptyMessage,
    children,
}: ChartStateProps) => {
    if (isLoading) {
        return (
            <div className="space-y-3" aria-busy="true">
                <div className="h-72 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
            </div>
        );
    }
    if (error) {
        return (
            <div className="rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-400">
                {error}
            </div>
        );
    }
    if (isEmpty) {
        return (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                {emptyMessage}
            </div>
        );
    }
    return <>{children}</>;
};

interface FXDetailPopupProps {
    symbol: string;
    onClose: () => void;
}

export const FXDetailPopup = ({ symbol, onClose }: FXDetailPopupProps) => {
    const navigate = useNavigate();
    const [tab, setTab] = useState<TabKey>('price');
    const [price, setPrice] = useState<PriceHistoryResponse | null>(null);
    const [factors, setFactors] = useState<FactorsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // COT history reuses the shared hook so the COT tab matches the standalone
    // pop-over's loading / error / empty semantics exactly.
    const {
        data: cot,
        isLoading: cotLoading,
        error: cotError,
    } = useCotHistory(symbol, HISTORY_DAYS);

    // Fetch price + factor history once per symbol, in parallel.
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setPrice(null);
        setFactors(null);
        (async () => {
            try {
                const [p, f] = await Promise.all([
                    fetchPriceHistory(symbol, HISTORY_DAYS),
                    fetchFactors(symbol, HISTORY_DAYS),
                ]);
                if (!cancelled) {
                    setPrice(p);
                    setFactors(f);
                }
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load FX detail', err);
                setError('Unable to load detail data for this currency.');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [symbol]);

    // Escape-to-close + body scroll lock, mirroring CotPopup.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    // Deep-dive entry point: close the modal and open the full FX Info Tear
    // Sheet (AFML frac-diff, volatility cones, seasonality, COT crowding) for
    // this currency, mirroring the commodities / crypto ranking "More Info…"
    // flow. Passes the internal FX symbol via the ?symbol= query param.
    const handleMoreInfo = () => {
        onClose();
        navigate(`/dashboard/fx-info-page?symbol=${encodeURIComponent(symbol)}`);
    };

    const priceSeries = price?.series ?? [];
    const factorSeries = factors?.series ?? [];
    const displayName = price?.name ?? factors?.name ?? cot?.name ?? symbol;
    const hasVolume = priceSeries.some(
        (b) => b.volume != null && b.volume > 0,
    );

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="relative w-full max-w-5xl rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`${symbol} detail`}
            >
                <button
                    type="button"
                    className="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition-colors"
                    onClick={onClose}
                    aria-label="Close"
                >
                    ✕
                </button>

                <h2 className="text-2xl font-bold text-slate-900 dark:text-white pr-8">
                    {displayName}
                </h2>
                <p className="mt-0.5 text-sm font-medium text-green-600 dark:text-emerald-400">
                    {symbol}
                    <span className="ml-2 rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                        USD numeraire
                    </span>
                </p>

                {/* Deep-dive entry point: opens the full FX Info Tear Sheet
                    (AFML fractional differencing, volatility cones, seasonality
                    and COT crowding z-scores) for this currency. */}
                <button
                    type="button"
                    onClick={handleMoreInfo}
                    className="mt-3 inline-flex items-center gap-1 rounded-lg bg-indigo-500 px-3 py-1.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:focus:ring-offset-slate-900"
                >
                    More Info… <span aria-hidden="true">&rarr;</span>
                </button>

                {/* Tab switcher. */}
                <div
                    className="mt-4 mb-5 flex flex-wrap gap-2"
                    role="tablist"
                    aria-label="FX detail views"
                >
                    {TABS.map((t) => {
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                role="tab"
                                aria-selected={active}
                                onClick={() => setTab(t.key)}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                    active
                                        ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900'
                                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
                                }`}
                            >
                                {t.label}
                            </button>
                        );
                    })}
                </div>

                {/* PRICE / VOLUME */}
                {tab === 'price' && (
                    <ChartState
                        isLoading={isLoading}
                        error={error}
                        isEmpty={priceSeries.length === 0}
                        emptyMessage="No price history is available for this currency."
                    >
                        <div className="w-full h-80 text-slate-500 dark:text-slate-400">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={priceSeries}
                                    margin={{
                                        top: 8,
                                        right: 8,
                                        bottom: 8,
                                        left: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="stroke-slate-200 dark:stroke-slate-700"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        yAxisId="price"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={56}
                                        domain={['auto', 'auto']}
                                        tickFormatter={(v) =>
                                            formatPrice(v as number)
                                        }
                                    />
                                    {hasVolume && (
                                        <YAxis
                                            yAxisId="volume"
                                            orientation="right"
                                            tick={{
                                                fontSize: 10,
                                                fill: 'currentColor',
                                            }}
                                            tickLine={false}
                                            width={48}
                                            tickFormatter={(v) =>
                                                formatVolume(v as number)
                                            }
                                        />
                                    )}
                                    <Tooltip
                                        formatter={(
                                            value: number | string,
                                            name: string,
                                        ) =>
                                            name === 'Volume (ticks)'
                                                ? formatVolume(value as number)
                                                : formatPrice(value as number)
                                        }
                                        contentStyle={{
                                            fontSize: 12,
                                            borderRadius: 8,
                                        }}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    {hasVolume && (
                                        <Bar
                                            yAxisId="volume"
                                            dataKey="volume"
                                            name="Volume (ticks)"
                                            barSize={6}
                                            fill="#f59e0b"
                                            opacity={0.45}
                                        />
                                    )}
                                    <Area
                                        yAxisId="price"
                                        type="monotone"
                                        dataKey="close"
                                        name="Close (USD)"
                                        stroke="#2563eb"
                                        fill="#93c5fd"
                                        fillOpacity={0.3}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                        {!hasVolume && (
                            <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
                                Tick volume is unavailable for this OTC pair
                                (FX feeds report no centralized turnover).
                            </p>
                        )}
                    </ChartState>
                )}

                {/* SIGNAL */}
                {tab === 'signal' && (
                    <ChartState
                        isLoading={isLoading}
                        error={error}
                        isEmpty={factorSeries.length === 0}
                        emptyMessage="No signal history is available for this currency."
                    >
                        <div className="w-full h-80 text-slate-500 dark:text-slate-400">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={factorSeries}
                                    margin={{
                                        top: 8,
                                        right: 16,
                                        bottom: 8,
                                        left: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="stroke-slate-200 dark:stroke-slate-700"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={48}
                                        tickFormatter={(v) =>
                                            formatZ(v as number)
                                        }
                                    />
                                    <Tooltip
                                        formatter={(value: number | string) =>
                                            formatZ(value as number)
                                        }
                                        contentStyle={{
                                            fontSize: 12,
                                            borderRadius: 8,
                                        }}
                                    />
                                    <ReferenceLine
                                        y={0}
                                        stroke="currentColor"
                                        strokeOpacity={0.3}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Line
                                        type="monotone"
                                        dataKey="forge_ultra_signal"
                                        name="Forge Ultra Signal"
                                        stroke="#06b6d4"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartState>
                )}

                {/* FACTORS */}
                {tab === 'factors' && (
                    <ChartState
                        isLoading={isLoading}
                        error={error}
                        isEmpty={factorSeries.length === 0}
                        emptyMessage="No factor history is available for this currency."
                    >
                        <div className="w-full h-80 text-slate-500 dark:text-slate-400">
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart
                                    data={factorSeries}
                                    margin={{
                                        top: 8,
                                        right: 8,
                                        bottom: 8,
                                        left: 8,
                                    }}
                                >
                                    <CartesianGrid
                                        strokeDasharray="3 3"
                                        className="stroke-slate-200 dark:stroke-slate-700"
                                    />
                                    <XAxis
                                        dataKey="date"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        minTickGap={40}
                                    />
                                    <YAxis
                                        yAxisId="z"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={48}
                                        tickFormatter={(v) =>
                                            formatZ(v as number)
                                        }
                                    />
                                    <YAxis
                                        yAxisId="vol"
                                        orientation="right"
                                        tick={{
                                            fontSize: 10,
                                            fill: 'currentColor',
                                        }}
                                        tickLine={false}
                                        width={48}
                                        tickFormatter={(v) =>
                                            `${((v as number) * 100).toFixed(0)}%`
                                        }
                                    />
                                    <Tooltip
                                        formatter={(
                                            value: number | string,
                                            name: string,
                                        ) =>
                                            name === 'Volatility (ann.)'
                                                ? `${((value as number) * 100).toFixed(1)}%`
                                                : formatZ(value as number)
                                        }
                                        contentStyle={{
                                            fontSize: 12,
                                            borderRadius: 8,
                                        }}
                                    />
                                    <ReferenceLine
                                        yAxisId="z"
                                        y={0}
                                        stroke="currentColor"
                                        strokeOpacity={0.3}
                                    />
                                    <Legend wrapperStyle={{ fontSize: 12 }} />
                                    <Bar
                                        yAxisId="vol"
                                        dataKey="annualized_volatility"
                                        name="Volatility (ann.)"
                                        barSize={6}
                                        fill="#94a3b8"
                                        opacity={0.35}
                                    />
                                    <Line
                                        yAxisId="z"
                                        type="monotone"
                                        dataKey="momentum_z_score"
                                        name="Momentum (z)"
                                        stroke="#2563eb"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        yAxisId="z"
                                        type="monotone"
                                        dataKey="fundamental_z_score"
                                        name="Commercials (z)"
                                        stroke="#0ea5e9"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    <Line
                                        yAxisId="z"
                                        type="monotone"
                                        dataKey="sentiment_z_score"
                                        name="Speculators (z)"
                                        stroke="#10b981"
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    </ChartState>
                )}

                {/* COT (reuses the extracted presentational body) */}
                {tab === 'cot' && (
                    <CotChartBody
                        symbol={symbol}
                        data={cot}
                        isLoading={cotLoading}
                        error={cotError}
                    />
                )}
            </div>
        </div>
    );
};

export default FXDetailPopup;
